/** Section 1 — Stages: the legal ladder (today) + the E&W bankruptcy route statuses (A2 engine). Keys are code; labels, durations and owner roles are Emma's. */
import { Input } from "@/components/ui/input";
import { Field, NumInput, EnumSelect, OWNER_ROLE_LABELS, SectionIntro, LiveBadge } from "./processShared";

export default function ProcessStagesSection({ body, setBody }) {
  const stages = body.stages || [];
  const update = (key, patch) => setBody((b) => ({ ...b, stages: b.stages.map((s) => (s.key === key ? { ...s, ...patch } : s)) }));
  const ladder = stages.filter((s) => s.ladder);
  const route = stages.filter((s) => !s.ladder);

  const Table = ({ rows, testPrefix }) => (
    <div className="border rounded-md overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-3 py-2 font-medium">Stage</th>
            <th className="text-left px-3 py-2 font-medium w-56">Label</th>
            <th className="text-left px-3 py-2 font-medium w-28">Expected days</th>
            <th className="text-left px-3 py-2 font-medium w-28">Amber after</th>
            <th className="text-left px-3 py-2 font-medium w-56">Default owner</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((s) => (
            <tr key={s.key} className={s.closed ? "opacity-70" : ""} data-testid={`${testPrefix}-${s.key}`}>
              <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">
                {s.key}{s.emma_stage != null && <span className="ml-1 text-slate-400">· Process stage {s.emma_stage}</span>}
                {s.closed && <span className="ml-1 text-slate-400">· closed</span>}
              </td>
              <td className="px-3 py-1.5"><Input value={s.label} onChange={(e) => update(s.key, { label: e.target.value })} className="h-7 text-xs" data-testid={`${testPrefix}-label-${s.key}`} /></td>
              <td className="px-3 py-1.5"><NumInput value={s.expected_days} min={0} max={365} onChange={(v) => update(s.key, { expected_days: v })} className="h-7 w-20" testId={`${testPrefix}-expected-${s.key}`} placeholder="—" /></td>
              <td className="px-3 py-1.5"><NumInput value={s.amber_after_days} min={0} max={365} onChange={(v) => update(s.key, { amber_after_days: v })} className="h-7 w-20" testId={`${testPrefix}-amber-${s.key}`} placeholder="—" /></td>
              <td className="px-3 py-1.5"><EnumSelect value={s.default_owner_role} onChange={(v) => update(s.key, { default_owner_role: v })} options={OWNER_ROLE_LABELS} className="h-7" testId={`${testPrefix}-owner-${s.key}`} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="process-stages-section">
      <div>
        <SectionIntro title="Legal ladder (live today)">
          The 23 stages the Legal Case Tracker uses now. Keys and order are fixed in code; rename them, set the expected duration and the
          day the stage turns amber, and choose who owns a stage's actions by default.
        </SectionIntro>
        <Table rows={ladder} testPrefix="stage" />
      </div>
      <div>
        <SectionIntro title="E&W personal bankruptcy route (live)" badge={<LiveBadge what="Live · A2 engine" />}>
          The seven-stage route — eligibility, pre-action, Statutory Demand, post-demand review, petition, hearing, order. Files are moved
          along it from the Customer File header and the Case progress map; each entry fires the matching Bankruptcy timeline step's "After this step"
          action (Legal settings → Timelines). Rename the statuses and set expected durations here.
        </SectionIntro>
        <Table rows={route} testPrefix="route" />
      </div>
      <Field label="Caseload target per handler" hint="Used by the Caseload view on the Legal Case Tracker — handlers above the target are flagged." className="max-w-xs">
        <NumInput value={body.thresholds?.caseload_target_per_handler} min={1} max={500} onChange={(v) => setBody((b) => ({ ...b, thresholds: { ...b.thresholds, caseload_target_per_handler: v } }))} testId="caseload-target" />
      </Field>
    </div>
  );
}
